import React from "react";
import {
  IoSparklesOutline,
  IoFlashOutline,
  IoStatsChartOutline,
  IoLayersOutline,
  IoTrendingUpOutline,
  IoCheckmarkCircleOutline,
} from "react-icons/io5"

export default function About() {
  return (
    <article className="animate-fade-in" data-page="about">
      {/* Header */}
      <header className="mb-6">
        <h2 className="text-3xl font-bold font-display text-white">About Me</h2>
        <div className="w-12 h-1 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full mt-2"></div>
      </header>

      {/* Intro Summary */}
      <section className="text-zinc-300 text-sm leading-relaxed space-y-4 font-sans">
        <p className="text-base text-zinc-200">
          I'm a <span className="text-white font-semibold underline decoration-violet-500/50 underline-offset-4">Full-Stack Engineer</span> with 2+ years of production experience building high-performance web applications, AI-powered systems, and <span className="text-violet-400 font-semibold">data-heavy analytical platforms</span>. Comfortable owning the full product lifecycle from Figma wireframes to AWS cloud deployment, I specialize in engineering responsive UI architectures with Next.js, TypeScript, and TanStack Query, while designing robust backends with Node.js, Python FastAPI, and PostgreSQL.
        </p>

        <p>
          Throughout my career, I've delivered mission-critical software across diverse engineering domains — from integrating <span className="text-violet-300 font-medium">LangChain and OpenAI APIs</span> to architect automated RAG pipelines and intelligent assistants at <strong className="text-white">Nspark Software</strong>, to optimizing complex analytics platforms like <strong className="text-white">Alphagen</strong> and <strong className="text-white">OptimizingAI</strong> at <strong className="text-white">WhatBytes</strong> (including a drag-and-drop filtering tool and a 30% latency reduction). At <strong className="text-white">ElPixala</strong>, I developed pixel-perfect, accessible frontends and streamlined headless CMS integrations for platforms like Acchawalacollege.
        </p>

        <p>
          Beyond enterprise work, I build end-to-end production-grade projects including <strong className="text-white">RAG Playground</strong> (a modular document ingestion and hybrid retrieval engine with Reciprocal Rank Fusion), <strong className="text-white">Wind Data Forecast</strong> (predictive wind power analytics via Next.js ISR and Recharts), <strong className="text-white">Puff Market</strong> (a decentralized Web3 NFT marketplace featuring ERC-1167 proxy factories that cut gas costs by 90–95%), and <strong className="text-white">Feather Docs</strong> (real-time collaborative editing with Socket.io).
        </p>
      </section>

      {/* Core Experience Showcase */}
      <section className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <IoTrendingUpOutline className="text-violet-400 text-lg" />
          <h3 className="text-xl font-semibold font-display text-white">Career Experience</h3>
        </div>

        <div className="space-y-4">
          {/* Nspark Software LLC */}
          <div className="glass-panel p-5 rounded-2xl border border-zinc-800/80 hover:border-violet-500/30 transition-all duration-300">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <h4 className="text-base font-bold text-white font-display">
                  AI Full-Stack Developer
                </h4>
                <p className="text-xs font-medium text-violet-400">
                  Nspark Software LLC
                </p>
              </div>
              <span className="text-[11px] font-mono text-zinc-400 bg-zinc-900 px-2.5 py-1 rounded-md border border-zinc-800">
                Jan 2026 – May 2026
              </span>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed font-sans mb-3">
              Owned end-to-end product development from Figma designs to AWS EC2 cloud deployment. Built a reusable React.js component library, integrated OpenAI APIs via LangChain to deploy automated RAG pipelines for document retrieval and in-app helper bots, and engineered dynamic data pipelines that convert file uploads and user metadata into automatic database schemas.
            </p>

            <div className="flex flex-wrap gap-1.5">
              {["LangChain", "RAG Pipeline", "OpenAI API", "React.js", "Node.js", "PostgreSQL", "AWS EC2"].map((tech) => (
                <span key={tech} className="text-[10px] font-medium text-zinc-400 bg-zinc-900/80 border border-zinc-800 px-2 py-0.5 rounded-full">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* WhatBytes */}
          <div className="glass-panel p-5 rounded-2xl border border-zinc-800/80 hover:border-indigo-500/30 transition-all duration-300">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <h4 className="text-base font-bold text-white font-display">
                  Full-Stack Engineer (SDE)
                </h4>
                <p className="text-xs font-medium text-indigo-400">
                  WhatBytes
                </p>
              </div>
              <span className="text-[11px] font-mono text-zinc-400 bg-zinc-900 px-2.5 py-1 rounded-md border border-zinc-800">
                May 2025 – Dec 2025
              </span>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed font-sans mb-3">
              Led frontend engineering for major platforms (<strong className="text-white">Alphagen</strong> & <strong className="text-white">OptimizingAI</strong>). Designed a complex drag-and-drop filtering tool supporting nested AND/OR conditions across dynamic attributes, engineered data-heavy analytical dashboards using Next.js & Recharts, built the Blujin LinkedIn Chrome extension for recruiters, created a generalized restaurant POS system from scratch in under a month, and reduced response times by <strong className="text-white">~30%</strong> via indexing and Zustand/TanStack Query caching.
            </p>

            <div className="flex flex-wrap gap-1.5">
              {["Next.js", "TypeScript", "TanStack Query", "Zustand", "Recharts", "Chrome Extension", "POS System"].map((tech) => (
                <span key={tech} className="text-[10px] font-medium text-zinc-400 bg-zinc-900/80 border border-zinc-800 px-2 py-0.5 rounded-full">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* ElPixala */}
          <div className="glass-panel p-5 rounded-2xl border border-zinc-800/80 hover:border-cyan-500/30 transition-all duration-300">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <h4 className="text-base font-bold text-white font-display">
                  Frontend Developer
                </h4>
                <p className="text-xs font-medium text-cyan-400">
                  ElPixala
                </p>
              </div>
              <span className="text-[11px] font-mono text-zinc-400 bg-zinc-900 px-2.5 py-1 rounded-md border border-zinc-800">
                May 2024 – Apr 2025
              </span>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed font-sans mb-3">
              Engineered production-ready, pixel-perfect React components from Figma wireframes for Acchawalacollege. Integrated Strapi headless CMS APIs with TanStack Query, built robust parsing algorithms to convert rich-text editor data into accessible semantic HTML, and optimized Core Web Vitals and SEO via lazy loading and code splitting.
            </p>

            <div className="flex flex-wrap gap-1.5">
              {["Next.js", "Tailwind CSS", "Strapi CMS", "TanStack Query", "SEO", "Web Vitals"].map((tech) => (
                <span key={tech} className="text-[10px] font-medium text-zinc-400 bg-zinc-900/80 border border-zinc-800 px-2 py-0.5 rounded-full">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Pros & Differentiators */}
      <section className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <IoSparklesOutline className="text-amber-400 text-lg" />
          <h3 className="text-xl font-semibold font-display text-white">What Makes The Difference</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Data-Heavy UI Optimization */}
          <div className="glass-panel p-5 rounded-2xl flex items-start gap-3.5 hover:border-zinc-700/60 transition-all">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 shrink-0">
              <IoStatsChartOutline className="text-xl" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white font-display">
                Data-Heavy UI & Dashboards
              </h4>
              <p className="text-xs text-zinc-400 leading-relaxed mt-1 font-sans">
                Specialized in architecting high-performance frontend interfaces for complex analytical platforms using Recharts, Next.js, and advanced data filtering algorithms.
              </p>
            </div>
          </div>

          {/* State & API Performance Tuning */}
          <div className="glass-panel p-5 rounded-2xl flex items-start gap-3.5 hover:border-zinc-700/60 transition-all">
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-400 shrink-0">
              <IoFlashOutline className="text-xl" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white font-display">
                State & Latency Optimization
              </h4>
              <p className="text-xs text-zinc-400 leading-relaxed mt-1 font-sans">
                Proven track record of improving responsiveness by ~30% using database indexing, TanStack Query caching, and Zustand client state management.
              </p>
            </div>
          </div>

          {/* Rapid Full-Stack Delivery */}
          <div className="glass-panel p-5 rounded-2xl flex items-start gap-3.5 hover:border-zinc-700/60 transition-all">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 shrink-0">
              <IoCheckmarkCircleOutline className="text-xl" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white font-display">
                Rapid Full-Stack Delivery
              </h4>
              <p className="text-xs text-zinc-400 leading-relaxed mt-1 font-sans">
                Complete ownership from Figma wireframes to AWS/Vercel cloud deployments, with a track record of shipping production applications in under 30 days.
              </p>
            </div>
          </div>

          {/* Web3 & Real-Time Engineering */}
          <div className="glass-panel p-5 rounded-2xl flex items-start gap-3.5 hover:border-zinc-700/60 transition-all">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
              <IoLayersOutline className="text-xl" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white font-display">
                Web3 & Real-time Systems
              </h4>
              <p className="text-xs text-zinc-400 leading-relaxed mt-1 font-sans">
                Deep experience with Solidity (ERC-1167 proxy factories saving 90-95% gas), Socket.io real-time multi-user syncing, and hybrid IPFS pipelines.
              </p>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}


